import * as React from 'react';
import useDebounceCallback from './use_debounce_callback';
var defaultOptionsArgMapFn = (o, i) => ({
  disabled: !!o.disabled,
  index: i,
  name: String(o.label || o.value).toLowerCase()
});
export default function useKeyPressOptionsHandler() {
  var optionsArg = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var callback = arguments.length > 1 ? arguments[1] : undefined;
  var delay = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 100;
  var optionsArgMapFunc = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : defaultOptionsArgMapFn;
  var iteratingMode = React.useRef(false);
  var iteratingModeCounter = React.useRef(0);
  var iteratingModeOptions = React.useRef([]);
  var keyBuffer = React.useRef([]);
  var lastKeyPressed = React.useRef();
  var clearKeyBuffer = useDebounceCallback(() => {
    keyBuffer.current = [];
  }, delay);
  var options = React.useMemo(() => {
    // Create a copy of select options containing its index within the obj
    return Array.isArray(optionsArg) && optionsArg.length ? optionsArg.map(optionsArgMapFunc) : [];
  }, [optionsArg, optionsArgMapFunc]);
  return React.useMemo(() => {
    var hasIndexChanged = () => {
      // Multiple select changes option indexes after selection
      var hasDifferentIndex = element => element.name !== options[element.index].name;
      return iteratingModeOptions.current.some(hasDifferentIndex);
    };
    var isDifferentKey = key => lastKeyPressed.current !== key;
    var isBufferEmpty = () => keyBuffer.current.length === 0;
    var enterIteratingMode = () => {
      if (!iteratingMode.current) {
        iteratingMode.current = true;
        iteratingModeCounter.current = 0;
        var s = keyBuffer.current.join('').toLowerCase();
        iteratingModeOptions.current = options.filter(o => o.name.startsWith(s) && !o.disabled);
      }
    };
    var leaveIteratingMode = () => {
      if (iteratingMode.current) {
        iteratingMode.current = false;
      }
    };
    var find = () => {
      var s = keyBuffer.current.join('').toLowerCase();
      return options.findIndex(o => o.name.startsWith(s) && !o.disabled);
    };
    var findInIteratingMode = () => {
      var c = iteratingModeOptions.current.length;
      if (c === 0) {
        lastKeyPressed.current = undefined;
        return -1;
      }
      iteratingModeCounter.current += 1;
      return iteratingModeOptions.current[iteratingModeCounter.current % c].index;
    };
    return key => {
      if (hasIndexChanged()) {
        // Update iteratingModeOptions if some option index has changed
        iteratingMode.current = false;
      }
      if (isDifferentKey(key)) {
        // Different or first typed key
        keyBuffer.current.push(key);
        lastKeyPressed.current = key;
        leaveIteratingMode();
        callback === null || callback === void 0 || callback(find());
      } else {
        // key pressed repeatedly ('a', 'a', ...), iterate over options starting with 'a'
        if (isBufferEmpty()) {
          keyBuffer.current.push(key);
        }
        lastKeyPressed.current = key;
        enterIteratingMode();
        callback === null || callback === void 0 || callback(findInIteratingMode());
      }
      clearKeyBuffer();
    };
  }, [callback, clearKeyBuffer, options]);
}